import React from 'react';

import {
	Box,
	Divider,
	type SxProps,
	type Theme,
	Typography,
} from '@mui/material';

import RouteLink from '@ivy/components/atoms/RouteLink';
import SupportLink from '@ivy/components/atoms/SupportLink';

const LearnMore = ({ sx }: { sx?: SxProps<Theme> }) => {
	return (
		<Box component='section' sx={sx}>
			<Typography component='h2' variant='h4' align='center'>
				Want to learn more?
			</Typography>
			<Box
				sx={{
					mt: {
						md: 10,
					},
					display: 'flex',
					flexDirection: {
						xs: 'column',
						md: 'row',
					},
					alignItems: {
						xs: 'center',
						md: 'initial',
					},
				}}
			>
				<RouteLink
					to={RouteLink.routes.FAQ_LIST}
					underline='none'
					color='inherit'
					sx={{
						py: {
							xs: 4,
							md: 0,
						},
						flexBasis: {
							md: '33%',
						},
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<svg
						width='81'
						height='80'
						viewBox='0 0 81 80'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path
							d='M40.5 6.40039C20.225 6.40039 3.69995 20.6066 3.69995 38.3941C3.69995 48.7129 9.34995 57.7004 18.0375 63.5691C18.025 63.9129 18.05 64.4754 17.6 66.1504C17.0375 68.2316 15.9 71.1566 13.6 74.4566L11.9625 76.8004H14.825C24.725 76.8004 30.4562 70.3441 31.3437 69.3066C34.2937 70.0004 37.325 70.3754 40.5 70.3754C60.7687 70.3754 77.2999 56.1691 77.2999 38.3879C77.2999 20.6066 60.775 6.40039 40.5 6.40039ZM40.5 9.60039C59.025 9.60039 74.1 22.5129 74.1 38.3941C74.1 54.2691 59.025 67.1816 40.5 67.1816C37.6687 67.1816 34.9125 66.8566 32.075 66.1941L30.175 65.7504L28.9125 67.2254C27.8187 68.5066 24.1375 72.2691 18.075 73.3254C19.2375 71.2379 20.1125 69.1129 20.6812 66.9816C21.1312 65.3316 21.2125 64.4629 21.2312 63.7004L21.3062 61.9191L19.825 60.9191C11.6125 55.3629 6.89995 47.1504 6.89995 38.3941C6.89995 22.5129 21.975 9.60039 40.5 9.60039ZM40.5 21.7566C37.3 21.7566 34.7437 22.7191 32.9812 24.6441C31.0625 26.5629 30.2625 29.1191 30.1 32.1566H34.1C34.2625 30.4004 34.5812 28.8004 35.0562 27.8441C36.0187 26.0816 37.7812 25.1191 40.3437 25.1191C42.2625 25.1191 43.8562 25.6004 44.6562 26.7191C45.4562 27.6816 45.9437 28.9629 45.9437 30.4004C45.9437 31.5191 45.6187 32.4816 44.9812 33.4441C44.6625 34.0816 44.1812 34.5629 43.7 35.0441L41.9437 36.8004C40.1812 38.5629 39.0625 40.0004 38.5812 41.2816C38.1 42.5629 37.7812 44.3191 37.7812 46.4004H41.7812C41.7812 44.4816 41.9375 43.2004 42.4187 42.4004C42.9 41.4379 43.8625 40.3191 45.3 38.8816C47.3812 36.9629 48.6625 35.5191 49.3 34.5566C49.9375 33.6004 50.2562 32.3191 50.2562 30.7191C50.2562 28.1566 49.2999 25.9191 47.5437 24.3191C45.7812 22.5566 43.3812 21.7566 40.5 21.7566ZM37.7812 50.0816V54.7191H42.1V50.0816H37.7812Z'
							fill='#2B2B2B'
						/>
					</svg>
					<Typography
						component='p'
						variant='h5'
						sx={{
							mt: 5,
							color: 'text.icon',
							textAlign: 'center',
						}}
					>
						Check out our FAQs
					</Typography>
				</RouteLink>
				<Divider
					sx={{
						display: {
							md: 'none',
						},
						width: '300px',
						maxWidth: '100%',
					}}
				/>
				<Box
					component={SupportLink}
					underline='none'
					color='inherit'
					sx={{
						py: {
							xs: 4,
							md: 0,
						},
						flexBasis: {
							md: '33%',
						},
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<svg
						width='80'
						height='80'
						viewBox='0 0 80 80'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path
							d='M30.4 0C13.7937 0 0 11.9875 0 27.05C0 34.3375 3.26875 40.9438 8.5 45.8C7.73125 48.0688 6.1 49.875 4.35 51.3C3.375 52.0938 2.425 52.775 1.65 53.45C1.2625 53.7875 0.90625 54.0875 0.6 54.55C0.29375 55.0125 -0.0125 55.8125 0.2 56.6L0.4 57.2L0.95 57.55C2.6125 58.4938 4.63125 58.65 6.75 58.5C8.86875 58.35 11.1562 57.8188 13.4 57.2C16.4938 56.35 19.1438 55.3375 21.35 54.45C25.75 64.625 36.7938 71.7 49.6 71.7C51.8188 71.7 53.9313 71.35 55.9 71.05C58.1313 72.0625 62.125 73.7312 66.45 74.9C70.9437 76.1188 75.5375 77.025 78.85 75.15L79.45 74.8L79.6 74.2C79.8125 73.4188 79.55 72.6688 79.25 72.2C78.95 71.7312 78.6312 71.3938 78.25 71.05C77.4875 70.3562 76.5188 69.6562 75.55 68.85C73.8063 67.4 72.1688 65.575 71.35 63.35C76.5625 58.4938 79.85 51.9188 79.85 44.65C79.85 32.7812 71.2 22.6562 59.35 19.05C55.5312 7.95 43.9313 0 30.4 0ZM30.4 3.2C45.475 3.2 57.45 13.9375 57.45 27.05C57.45 40.15 45.325 50.9 30.25 50.9C28.0875 50.9 25.9625 50.7438 24.05 50.3L23.55 50.15L23.05 50.35C22.8625 50.4313 22.4688 50.6063 22.25 50.7C22.2125 50.7125 22.1375 50.6875 22.1 50.7C21.725 50.725 21.3688 50.8875 21.1 51.15C18.8375 52.0812 15.8562 53.2438 12.55 54.15C10.4312 54.7313 8.29375 55.1688 6.5 55.3C5.70625 55.3563 5.375 55.1188 4.75 55.05C5.2625 54.65 5.69375 54.3312 6.35 53.8C8.48125 52.0625 11.1062 49.55 11.95 45.8L12.15 44.85L11.45 44.2C6.33125 39.8312 3.2 33.75 3.2 27.05C3.2 13.95 15.325 3.2 30.4 3.2ZM60.25 22.7C60.55 22.8125 60.8563 22.925 61.15 23.05C60.9438 23.3188 60.8 23.6375 60.8 24C60.8 24.8813 61.5188 25.6 62.4 25.6C63.1375 25.6 63.7125 25.075 63.9 24.4C67.45 26.3625 70.4125 29.0188 72.6 32.15C72.4062 32.0688 72.2188 32 72 32C71.1188 32 70.4 32.7188 70.4 33.6C70.4 34.4813 71.1188 35.2 72 35.2C72.85 35.2 73.5438 34.5375 73.6 33.7C74.3375 34.95 74.975 36.2875 75.45 37.65C75.3187 37.8812 75.2 38.1188 75.2 38.4C75.2 39 75.5625 39.475 76.05 39.75C76.4313 41.3375 76.65 42.9688 76.65 44.65C76.65 45.25 76.6 45.8563 76.55 46.45C75.7938 46.575 75.2 47.2062 75.2 48C75.2 48.625 75.5813 49.1375 76.1 49.4C75 54.2 72.2812 58.4938 68.4 61.8L67.65 62.45L67.9 63.45C68.7125 66.6125 70.7063 68.8625 72.6 70.55C72.4062 70.4688 72.2188 70.4 72 70.4C71.1188 70.4 70.4 71.1188 70.4 72C70.4 72.1625 70.4563 72.3 70.5 72.45C69.4812 72.3125 68.4313 72.1062 67.3 71.8C63.875 70.875 60.5375 69.5563 58.3 68.6C58.8188 68.3375 59.2 67.825 59.2 67.2C59.2 66.3187 58.4813 65.6 57.6 65.6C56.7188 65.6 56 66.3187 56 67.2C56 67.4188 56.0688 67.6062 56.15 67.8L55.9 67.85C53.8312 68.1688 51.6875 68.5 49.6 68.5C49.3688 68.5 49.1312 68.5062 48.9 68.5C49.3125 68.2125 49.6 67.7438 49.6 67.2C49.6 66.3187 48.8812 65.6 48 65.6C47.1188 65.6 46.4 66.3187 46.4 67.2C46.4 67.6438 46.5625 68.0625 46.85 68.35C44.4438 68.1375 42.125 67.675 39.95 66.95C39.825 66.1937 39.1938 65.6 38.4 65.6C38.0187 65.6 37.675 65.7312 37.4 65.95C36.1 65.375 34.8688 64.7438 33.7 64C34.5375 63.9438 35.2 63.25 35.2 62.4C35.2 61.5188 34.4813 60.8 33.6 60.8C32.7188 60.8 32 61.5188 32 62.4C32 62.5812 32.0438 62.7375 32.1 62.9C30.475 61.6938 29.0125 60.3 27.75 58.8C28.0312 59.0438 28.4 59.2 28.8 59.2C29.6812 59.2 30.4 58.4813 30.4 57.6C30.4 56.7188 29.6812 56 28.8 56C27.9188 56 27.2 56.7188 27.2 57.6C27.2 57.9625 27.3438 58.2812 27.55 58.55C26.4375 57.1875 25.4625 55.75 24.7 54.2C24.95 54.075 25.15 53.8813 25.3 53.65C26.9375 53.9062 28.6 54.1 30.25 54.1C31 54.1 31.7625 54.05 32.5 54C32.7875 54.2625 33.1813 54.4 33.6 54.4C34.1188 54.4 34.5563 54.1375 34.85 53.75C37.2875 53.4188 39.6312 52.8688 41.85 52.05C41.7188 52.2812 41.6 52.5188 41.6 52.8C41.6 53.6813 42.3188 54.4 43.2 54.4C44.0812 54.4 44.8 53.6813 44.8 52.8C44.8 52.0625 44.275 51.4875 43.6 51.3C44.8812 50.7375 46.125 50.1125 47.3 49.4C47.5187 49.5125 47.7375 49.6 48 49.6C48.8812 49.6 49.6 48.8812 49.6 48C49.6 47.9625 49.55 47.9375 49.55 47.9C50.875 46.925 52.125 45.8563 53.25 44.7C53.8 44.5312 54.1813 44.0688 54.3 43.5C55.3063 42.3438 56.2625 41.15 57.05 39.85C57.2313 39.9188 57.3938 40 57.6 40C58.4813 40 59.2 39.2812 59.2 38.4C59.2 37.8375 58.8875 37.3875 58.45 37.1C59.85 34 60.65 30.6125 60.65 27.05C60.65 25.5688 60.5063 24.1125 60.25 22.7ZM67.2 27.2C66.3187 27.2 65.6 27.9188 65.6 28.8C65.6 29.6812 66.3187 30.4 67.2 30.4C68.0813 30.4 68.8 29.6812 68.8 28.8C68.8 27.9188 68.0813 27.2 67.2 27.2ZM62.4 32C61.5188 32 60.8 32.7188 60.8 33.6C60.8 34.4813 61.5188 35.2 62.4 35.2C63.2812 35.2 64 34.4813 64 33.6C64 32.7188 63.2812 32 62.4 32ZM67.2 36.8C66.3187 36.8 65.6 37.5187 65.6 38.4C65.6 39.2812 66.3187 40 67.2 40C68.0813 40 68.8 39.2812 68.8 38.4C68.8 37.5187 68.0813 36.8 67.2 36.8ZM62.4 41.6C61.5188 41.6 60.8 42.3188 60.8 43.2C60.8 44.0812 61.5188 44.8 62.4 44.8C63.2812 44.8 64 44.0812 64 43.2C64 42.3188 63.2812 41.6 62.4 41.6ZM72 41.6C71.1188 41.6 70.4 42.3188 70.4 43.2C70.4 44.0812 71.1188 44.8 72 44.8C72.8812 44.8 73.6 44.0812 73.6 43.2C73.6 42.3188 72.8812 41.6 72 41.6ZM57.6 46.4C56.7188 46.4 56 47.1188 56 48C56 48.8812 56.7188 49.6 57.6 49.6C58.4813 49.6 59.2 48.8812 59.2 48C59.2 47.1188 58.4813 46.4 57.6 46.4ZM67.2 46.4C66.3187 46.4 65.6 47.1188 65.6 48C65.6 48.8812 66.3187 49.6 67.2 49.6C68.0813 49.6 68.8 48.8812 68.8 48C68.8 47.1188 68.0813 46.4 67.2 46.4ZM52.8 51.2C51.9188 51.2 51.2 51.9188 51.2 52.8C51.2 53.6813 51.9188 54.4 52.8 54.4C53.6813 54.4 54.4 53.6813 54.4 52.8C54.4 51.9188 53.6813 51.2 52.8 51.2ZM62.4 51.2C61.5188 51.2 60.8 51.9188 60.8 52.8C60.8 53.6813 61.5188 54.4 62.4 54.4C63.2812 54.4 64 53.6813 64 52.8C64 51.9188 63.2812 51.2 62.4 51.2ZM72 51.2C71.1188 51.2 70.4 51.9188 70.4 52.8C70.4 53.6813 71.1188 54.4 72 54.4C72.8812 54.4 73.6 53.6813 73.6 52.8C73.6 51.9188 72.8812 51.2 72 51.2ZM38.4 56C37.5187 56 36.8 56.7188 36.8 57.6C36.8 58.4813 37.5187 59.2 38.4 59.2C39.2812 59.2 40 58.4813 40 57.6C40 56.7188 39.2812 56 38.4 56ZM48 56C47.1188 56 46.4 56.7188 46.4 57.6C46.4 58.4813 47.1188 59.2 48 59.2C48.8812 59.2 49.6 58.4813 49.6 57.6C49.6 56.7188 48.8812 56 48 56ZM57.6 56C56.7188 56 56 56.7188 56 57.6C56 58.4813 56.7188 59.2 57.6 59.2C58.4813 59.2 59.2 58.4813 59.2 57.6C59.2 56.7188 58.4813 56 57.6 56ZM67.2 56C66.3187 56 65.6 56.7188 65.6 57.6C65.6 58.4813 66.3187 59.2 67.2 59.2C68.0813 59.2 68.8 58.4813 68.8 57.6C68.8 56.7188 68.0813 56 67.2 56ZM43.2 60.8C42.3188 60.8 41.6 61.5188 41.6 62.4C41.6 63.2812 42.3188 64 43.2 64C44.0812 64 44.8 63.2812 44.8 62.4C44.8 61.5188 44.0812 60.8 43.2 60.8ZM52.8 60.8C51.9188 60.8 51.2 61.5188 51.2 62.4C51.2 63.2812 51.9188 64 52.8 64C53.6813 64 54.4 63.2812 54.4 62.4C54.4 61.5188 53.6813 60.8 52.8 60.8ZM62.4 60.8C61.5188 60.8 60.8 61.5188 60.8 62.4C60.8 63.2812 61.5188 64 62.4 64C63.2812 64 64 63.2812 64 62.4C64 61.5188 63.2812 60.8 62.4 60.8ZM67.2 65.6C66.3187 65.6 65.6 66.3187 65.6 67.2C65.6 68.0813 66.3187 68.8 67.2 68.8C68.0813 68.8 68.8 68.0813 68.8 67.2C68.8 66.3187 68.0813 65.6 67.2 65.6ZM73.3 71.1C73.3688 71.1562 73.4313 71.2438 73.5 71.3C74.1625 71.85 74.6375 72.1875 75.15 72.6C74.6125 72.65 74.025 72.6562 73.4 72.65C73.5 72.4437 73.6 72.2438 73.6 72C73.6 71.6625 73.4812 71.3562 73.3 71.1Z'
							fill='#2B2B2B'
						/>
					</svg>
					<Typography
						component='p'
						variant='h5'
						sx={{
							mt: 5,
							color: 'text.icon',
							textAlign: 'center',
						}}
					>
						Send us a message
					</Typography>
				</Box>
				<Divider
					sx={{
						display: {
							md: 'none',
						},
						width: '300px',
						maxWidth: '100%',
					}}
				/>
				<RouteLink
					to='https://calendly.com/leon-adelman/zoom-chat'
					underline='none'
					color='inherit'
					openInNewTab
					sx={{
						pt: {
							xs: 4,
							md: 0,
						},
						flexBasis: {
							md: '33%',
						},
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<svg
						width='80'
						height='80'
						viewBox='0 0 80 80'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<g clipPath='url(#clip0_306_443)'>
							<path
								d='M48.1468 3.2C45.4303 3.2 43.2 5.44234 43.2 8.15937V25.6C43.2 28.233 45.3762 30.4 48 30.4H50.9437C50.8601 30.8995 50.8628 31.602 50.7 31.9406C50.368 32.6309 49.8602 33.1745 48.775 33.8281C48.4749 34.0083 48.2423 34.2819 48.1126 34.607C47.983 34.932 47.9634 35.2906 48.0571 35.6279C48.1507 35.9651 48.3522 36.2623 48.6309 36.474C48.9096 36.6857 49.25 36.8002 49.6 36.8C51.5259 36.8 53.7311 36.426 55.775 35.2875C57.5219 34.3144 59.0178 32.6167 60.0968 30.4H75.2C77.8237 30.4 80 28.233 80 25.6V8.15937C80 5.44234 77.7696 3.2 75.0531 3.2H48.1468ZM6.39995 6.4C4.63515 6.4 3.19995 7.8352 3.19995 9.6V57.6C3.19995 59.3648 4.63515 60.8 6.39995 60.8H30.4V65.6H25.6C22.9536 65.6 20.8 67.7536 20.8 70.4V73.6H59.2V70.4C59.2 67.7536 57.0464 65.6 54.4 65.6H49.6V60.8H73.6C75.3648 60.8 76.7999 59.3648 76.7999 57.6V33.6H73.6L73.6031 57.6H6.39995V51.2H73.6V48H6.39995V9.6H40V6.4H6.39995ZM48.1468 6.4H75.0531C76.0166 6.4 76.7999 7.1804 76.7999 8.15937V25.6C76.7999 26.663 76.2562 27.2 75.2 27.2H59.2C58.872 27.1999 58.5519 27.3006 58.2831 27.4884C58.0142 27.6762 57.8096 27.942 57.6968 28.25C56.8996 30.4336 55.6576 31.6922 54.2187 32.4937C54.0609 32.5816 53.8754 32.5394 53.7125 32.6156C54.1357 31.4517 54.4 30.2461 54.4 28.8C54.3999 28.3757 54.2313 27.9687 53.9313 27.6687C53.6312 27.3686 53.2243 27.2 52.8 27.2H48C46.9437 27.2 46.4 26.663 46.4 25.6V8.15937C46.4 7.1804 47.1833 6.4 48.1468 6.4ZM53.6 14.4C52.9634 14.4 52.353 14.6529 51.9029 15.1029C51.4528 15.553 51.2 16.1635 51.2 16.8C51.2 17.4365 51.4528 18.047 51.9029 18.4971C52.353 18.9471 52.9634 19.2 53.6 19.2C54.2365 19.2 54.8469 18.9471 55.297 18.4971C55.7471 18.047 56 17.4365 56 16.8C56 16.1635 55.7471 15.553 55.297 15.1029C54.8469 14.6529 54.2365 14.4 53.6 14.4ZM61.6 14.4C60.9634 14.4 60.353 14.6529 59.9029 15.1029C59.4528 15.553 59.2 16.1635 59.2 16.8C59.2 17.4365 59.4528 18.047 59.9029 18.4971C60.353 18.9471 60.9634 19.2 61.6 19.2C62.2365 19.2 62.8469 18.9471 63.297 18.4971C63.7471 18.047 64 17.4365 64 16.8C64 16.1635 63.7471 15.553 63.297 15.1029C62.8469 14.6529 62.2365 14.4 61.6 14.4ZM69.6 14.4C68.9634 14.4 68.353 14.6529 67.9029 15.1029C67.4528 15.553 67.2 16.1635 67.2 16.8C67.2 17.4365 67.4528 18.047 67.9029 18.4971C68.353 18.9471 68.9634 19.2 69.6 19.2C70.2365 19.2 70.8469 18.9471 71.297 18.4971C71.7471 18.047 72 17.4365 72 16.8C72 16.1635 71.7471 15.553 71.297 15.1029C70.8469 14.6529 70.2365 14.4 69.6 14.4ZM65.6 52.8V56H68.7999V52.8H65.6ZM33.6 60.8H46.4V65.6H33.6V60.8ZM25.6 68.8H54.4C55.2816 68.8 56 69.5184 56 70.4H24C24 69.5184 24.7184 68.8 25.6 68.8Z'
								fill='#2B2B2B'
							/>
						</g>
						<defs>
							<clipPath id='clip0_306_443'>
								<rect width='80' height='80' fill='white' />
							</clipPath>
						</defs>
					</svg>
					<Typography
						component='p'
						variant='h5'
						sx={{
							mt: 5,
							color: 'text.icon',
							textAlign: 'center',
						}}
					>
						Meet with a hiring advocate
					</Typography>
				</RouteLink>
			</Box>
		</Box>
	);
};

export default LearnMore;
